import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import roleApiProvider from '../apiProvider/roleApi';
import userApiProvider from '../apiProvider/userApi';
import chapterApiProvider from '../apiProvider/chapterApi';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { toast, ToastContainer } from 'react-toastify';
import { IMAGE_BASE_URL } from '../network/apiClient';
import { getCurrentUser } from '../utils/auth';


const AddUserLayer = () => {
  const loggedInUser = getCurrentUser()?.data;
  const rawRole = loggedInUser?.role;
  const roleName = (typeof rawRole === 'object' ? rawRole?.name : rawRole) || '';
  const roleNameLower = roleName.toLowerCase();
  const isSuperAdmin = roleNameLower === 'admin' || roleNameLower === 'super admin' || roleNameLower === 'super-admin';
  const isED = roleNameLower === 'ed' || roleNameLower === 'executive director';
  const isZoneAdmin = roleNameLower === 'zone-admin';

  const getRestrictedZoneId = () => {
    if (isSuperAdmin) return null;
    return loggedInUser?.zoneId?._id || loggedInUser?.zoneId || null;
  };

  // Special sentinel for "All Chapters in Zone"
  const ALL_CHAPTERS_OPTION = { value: 'ALL', label: '⭐ All Chapters (All zones chapters)' };

    const { id } = useParams();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const initialZoneId = queryParams.get("zoneId");
    const initialRole = queryParams.get("role");

    const isEditMode = !!id;
    const navigate = useNavigate();
    const [imagePreviewUrl, setImagePreviewUrl] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [roleOptions, setRoleOptions] = useState([]);
    const [zones, setZones] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showPin, setShowPin] = useState(false);
    const [error, setError] = useState({});
    const [userData, setUserData] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        companyName: '',
        email: '',
        username: '',
        pin: '',
        mobileNumber: '',
        role: initialRole || '',
        zoneId: initialZoneId || getRestrictedZoneId() || '',
        chapterId: []
    });

    useEffect(() => {
        fetchRoleData();
        fetchZoneData();
        if (isEditMode) {
            fetchUserData();
        }
    }, [id]);

    const fetchRoleData = async () => {
        try {
            const response = await roleApiProvider.getRoles();
            if (response && response.status) {
                const options = response.response.data.map((item) => ({
                    label: item._id,
                    value: item.name
                }));
                setRoleOptions(options);
            }
        } catch (error) {
            console.error("Error fetching roles:", error);
        }
    };

    const fetchZoneData = async () => {
        try {
            const response = await chapterApiProvider.getZones({ limit: 100 });
            console.log("FETCHED ZONES in AddUserLayer:", response);
            if (response && response.status) {
                const data = response.response?.data || response.response || [];
                setZones(Array.isArray(data) ? data : (data.zones || []));
            }
        } catch (error) {
            console.error("Error fetching zones:", error);
        }
    };

    useEffect(() => {
        if (formData.zoneId) {
            fetchChaptersByZone(formData.zoneId);
        } else {
            setChapters([]);
        }
    }, [formData.zoneId]);

    const fetchChaptersByZone = async (zoneId) => {
        try {
            const response = await chapterApiProvider.getChaptersByZone(zoneId);
            if (response && response.status) {
                const data = response.response?.data || response.response || [];
                const allChapters = Array.isArray(data) ? data : (data.chapters || []);
                let filteredChapters = allChapters.filter(c => c.isActive === 1);

                // Filter chapters based on logged-in user's access
                if (!isSuperAdmin && !isED && !isZoneAdmin) {
                    const userChapters = loggedInUser?.chapterId || loggedInUser?.chapterIds || [];
                    const userChapterIds = Array.isArray(userChapters)
                        ? userChapters.map(c => typeof c === 'object' ? c._id || c.id : c)
                        : [typeof userChapters === 'object' ? userChapters._id || userChapters.id : userChapters];
                    filteredChapters = filteredChapters.filter(c => userChapterIds.includes(c._id));
                }

                setChapters(filteredChapters);
            }
        } catch (error) {
            console.error("Error fetching chapters:", error);
            setChapters([]);
        }
    };

    const fetchUserData = async () => {
        setLoading(true);
        try {
            const response = await userApiProvider.getUserById(id);
            console.log(response, "response-getUserById");
            
            if (response && response.status) {
                const user = response.response.data;
                setFormData({
                    name: user.name,
                    companyName: user.companyName,
                    email: user.email,
                    username: user.username,
                    pin: '', // Don't pre-fill PIN for security
                    mobileNumber: user.mobileNumber,
                    role: user?.role._id,
                    zoneId: user?.zoneId?._id || user?.zoneId || '',
                    // If allChapters flag is set, pre-fill with the special sentinel
                    chapterId: user?.allChapters ? ['ALL'] : (user?.chapterId || []),
                    image: user.profileImage || ''
                });
                if (user.profileImage) {
                    // setImagePreviewUrl(user.profileImage);
                }
            }
        } catch (error) {
            console.error("Error fetching user:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;

        // Validate PIN input (only digits, max 4 characters)
        if (id === 'pin' && !/^\d{0,4}$/.test(value)) {
            return;
        }

        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleRoleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            role: e.target.value
        }));
    };

    const togglePinVisibility = () => {
        setShowPin(!showPin);
    };

    const handleSubmit = async (e) => {
        console.log("handleSubmit called", formData, "isEditMode:", isEditMode);
        e.preventDefault();

        // Validate required fields
        e.preventDefault();
        const errors = {}
        // Validate required fields
        if (!formData.name?.trim()) {
            errors.name = "Please enter your name";
            setError(errors)
            return false
        } else if (!/^[a-zA-Z\s]+$/.test(formData.name)) {
            errors.name = "Name should contain only alphabets";
            setError(errors)
            return false
        }

        // Validate Company Name
        if (!formData.companyName?.trim()) {
            errors.companyName = "Please enter company name";
            setError(errors)
            return false
        } else if (!/^[a-zA-Z0-9\s\-,.&'()]+$/.test(formData.companyName)) {
            errors.companyName = "Company name contains invalid characters";
            setError(errors)
            return false
        }

        // Validate Mobile Number (exactly 10 digits)
        if (!formData.mobileNumber?.trim()) {
            errors.mobileNumber = "Please enter mobile number";
            setError(errors)
            return false
        } else if (!/^[0-9]{10}$/.test(formData.mobileNumber)) {
            errors.mobileNumber = "Mobile number must be 10 digits";
            setError(errors)
            return false
        }

        // Validate Email
        if (!formData.email?.trim()) {
            errors.email = "Please enter email";
            setError(errors)
            return false
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = "Please enter a valid email";
            setError(errors)
            return false
        }

        // Username is automatically set to name

        // Validate PIN (exactly 4 digits)
        if(!isEditMode){
            if (!formData.pin?.trim()) {
                errors.pin = "Please enter PIN";
                setError(errors)
                return false
            }
            if (!/^[0-9]{4}$/.test(formData.pin)) {
                errors.pin = "PIN must be exactly 4 digits";
                setError(errors)
                return false
            }
        }
        // if (!formData.pin?.trim()) {
        //     errors.pin = "Please enter PIN";
        //     setError(errors)
        //     return false
        // }
        // if (!/^[0-9]{4}$/.test(formData.pin)) {
        //     errors.pin = "PIN must be exactly 4 digits";
        //     setError(errors)
        //     return false
        // }

        // Validate Role
        if (!formData.role) {
            errors.role = "Please select a role";
            setError(errors)
            return false
        }
        setLoading(true);

        // Validate PIN for new users
        if (!isEditMode && (!formData.pin || !/^\d{4}$/.test(formData.pin))) {
            Swal.fire({
                title: 'Error!',
                text: 'PIN must be exactly 4 digits',
                icon: 'error',
                customClass: {
                    popup: 'small-swal-popup',
                    title: 'small-swal-title',
                    htmlContainer: 'small-swal-text'
                },
                width: '400px'
            });
            return;
        }

        setLoading(true);

        // Create FormData object
        const formDataToSend = new FormData();
        formDataToSend.append('name', formData.name);
        formDataToSend.append('companyName', formData.companyName);
        formDataToSend.append('email', formData.email);
        formDataToSend.append('username', formData.name);
        formDataToSend.append('pin', formData.pin);
        formDataToSend.append('mobileNumber', formData.mobileNumber);
        formDataToSend.append('role', formData.role);
        
        if (formData.zoneId) {
            formDataToSend.append('zoneId', formData.zoneId);
        }
        if (formData.chapterId && formData.chapterId.length > 0) {
            formData.chapterId.forEach(id => formDataToSend.append('chapterId', id));
        }

        // Append the file if selected
        if (selectedFile) {
            formDataToSend.append('profileImage', selectedFile);
        }
        console.log(...formDataToSend, "formDataToSend");
        // return
        try {
            let response;
            if (isEditMode) {
                response = await userApiProvider.updateUser(id, formDataToSend);
            } else {
                response = await userApiProvider.addUsers(formDataToSend);
            }

            if (response && response.status) {
                Swal.fire({
                    title: 'Success!',
                    text: `User ${isEditMode ? 'updated' : 'created'} successfully.`,
                    icon: 'success',
                    customClass: {
                        popup: 'small-swal-popup',
                        title: 'small-swal-title',
                        htmlContainer: 'small-swal-text'
                    },
                    width: '400px'
                }).then(() => {
                    navigate("/users-list");
                });
            }
            else {
                console.log(response)
                toast.error(response?.response)
            }
        } catch (error) {
            console.log(error, "error")
            // Show more detailed error message
            toast.error(error.response?.data?.message ||
                error.message ||
                `An error occurred while ${isEditMode ? 'updating' : 'creating'} user`);
        } finally {
            setLoading(false);
        }
    };
console.log(formData, "formData");
console.log("imagePreviewUrl", imagePreviewUrl);


    // useEffect(() => {
    //     const fetchUserData = async () => {
    //         const storedUserData = JSON.parse(localStorage.getItem("userData"));
    //         if (storedUserData) {
    //             const response = await userApiProvider.getUserById(storedUserData?.id);
    //             if (response && response.status) {
    //                 const data = response.response.data;
    //                 console.log(data, "data")
    //                 setUserData(data);

    //                 // Set initial form values from user data
    //                 // setFormData({
    //                 //     firstName: data.personalDetails?.firstName || '',
    //                 //     lastName: data.personalDetails?.lastName || '',
    //                 //     companyName: data.personalDetails?.companyName || '',
    //                 //     mobileNumber: data.contactDetails?.mobileNumber || '',
    //                 //     email: data.contactDetails?.email || '',
    //                 //     image: data.personalDetails?.profileImage || ''
    //                 //     // username: '', // Add if available in your data
    //                 //     // password: ''  // Typically you wouldn't pre-fill passwords
    //                 // });

    //                 // Set initial image if available
    //                 if (data.profileImageUrl) {
    //                     setImagePreviewUrl(data.profileImageUrl);
    //                 }
    //             }
    //         }
    //     };

    //     fetchUserData();
    // }, []);

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-body p-24">
                <div className="row justify-content-center">
                    <div className="col-xxl-8 col-xl-10 col-lg-12">
                        <div className="card border">
                            <div className="card-body">
                                <h6 className="text-md text-primary-light mb-16">Profile Image</h6>
                                <div className="mb-24 mt-16">
                                    <div className="avatar-upload">
                                        <div className="avatar-edit position-absolute bottom-0 end-0 me-24 mt-16 z-1 cursor-pointer">
                                            <input
                                                type="file"
                                                id="imageUpload"
                                                accept=".png, .jpg, .jpeg"
                                                hidden
                                                onChange={handleImageChange}
                                                disabled={loading}
                                            />
                                            <label
                                                htmlFor="imageUpload"
                                                className="w-32-px h-32-px d-flex justify-content-center align-items-center bg-primary-50 text-primary-600 border border-primary-600 bg-hover-primary-100 text-lg rounded-circle">
                                                <Icon icon="solar:camera-outline" className="icon"></Icon>
                                            </label>
                                        </div>
                                        <div className="avatar-preview">
                                            <div
                                                id="imagePreview"
                                                style={{
                                                    backgroundImage: imagePreviewUrl ? `url(${imagePreviewUrl})` : formData?.image ? `url(${IMAGE_BASE_URL}/${formData.image.docPath}/${formData.image.docName})` :
                                                        '',
                                                    backgroundSize: 'cover',
                                                    backgroundPosition: 'center'
                                                }}
                                            >
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <form onSubmit={handleSubmit}>
                                    <div className="row">
                                    <div className="col-md-6 mb-20">
                                        <label htmlFor="name" className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Name <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control radius-8"
                                            id="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            disabled={loading}
                                        />
                                        {error && error.name ? <span className='text-danger'>{error.name}</span> : <></>}
                                    </div>

                                    <div className="col-md-6 mb-20">
                                        <label htmlFor="companyName" className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Company Name <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control radius-8"
                                            id="companyName"
                                            value={formData.companyName}
                                            onChange={handleInputChange}
                                            disabled={loading}
                                        />
                                        {error && error.companyName ? <span className='text-danger'>{error.companyName}</span> : <></>}
                                    </div>

                                    <div className="col-md-6 mb-20">
                                        <label htmlFor="mobileNumber" className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Mobile Number <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="tel"
                                            className="form-control radius-8"
                                            id="mobileNumber"
                                            value={formData.mobileNumber}
                                            onChange={handleInputChange}
                                            disabled={loading}
                                        />
                                        {error && error.mobileNumber ? <span className='text-danger'>{error.mobileNumber}</span> : <></>}
                                    </div>

                                    <div className="col-md-6 mb-20">
                                        <label htmlFor="email" className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Email <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            className="form-control radius-8"
                                            id="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            disabled={loading}
                                        />
                                        {error && error.email ? <span className='text-danger'>{error.email}</span> : <></>}
                                    </div>

                                    {/* Username field removed - name is used instead */}

                                    {!isEditMode && (
                                        <div className="col-md-6 mb-20">
                                            <label htmlFor="pin" className="form-label fw-semibold text-primary-light text-sm mb-8">
                                                PIN <span className="text-danger-600">*</span>
                                            </label>
                                            <div className="position-relative">
                                                <input
                                                    type={showPin ? "text" : "password"}
                                                    className="form-control radius-8"
                                                    id="pin"
                                                    placeholder="Enter 4-digit PIN"
                                                    value={formData.pin}
                                                    onChange={handleInputChange}
                                                    maxLength="4"
                                                    pattern="\d{4}"
                                                    disabled={loading}
                                                />
                                                <span
                                                    className="toggle-password ri-eye-line cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light"
                                                    onClick={togglePinVisibility}
                                                >
                                                    {/* <Icon icon={showPin ? "mdi:eye-off" : "mdi:eye"} /> */}
                                                </span>
                                                {error && error.pin ? <span className='text-danger'>{error.pin}</span> : <></>}
                                            </div>
                                            <small className="text-muted">Must be exactly 4 digits (numbers only)</small>
                                        </div>
                                    )}

                                    <div className="col-md-6 mb-20">
                                        <label htmlFor="role" className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Role <span className="text-danger-600">*</span>
                                        </label>
                                        <select
                                            className="form-control radius-8 form-select"
                                            id="role"
                                            value={formData.role}
                                            onChange={handleRoleChange}
                                            disabled={loading}
                                        >
                                            <option value="">Select Role</option>
                                            {roleOptions.map((option) => (
                                                <option key={option.label} value={option.label}>
                                                    {option.value}
                                                </option>
                                            ))}
                                        </select>
                                        {error && error.role ? <span className='text-danger'>{error.role}</span> : <></>}
                                    </div>

                                    {/* Zone Selection */}
                                    <div className="col-md-6 mb-20">
                                        <label htmlFor="zoneId" className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Zone
                                        </label>
                                        <select
                                            className="form-control radius-8 form-select"
                                            id="zoneId"
                                            value={formData.zoneId}
                                            onChange={handleInputChange}
                                            disabled={loading || !isSuperAdmin}
                                        >
                                            <option value="">Select Zone</option>
                                            {zones.map((zone) => (
                                                <option key={zone._id} value={zone._id}>
                                                    {zone.zoneName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Chapter Selection */}
                                    <div className="col-md-6 mb-20">
                                        <label htmlFor="chapterId" className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Chapter
                                        </label>
                                        <Select
                                            isMulti
                                            id="chapterId"
                                            options={
                                                (isSuperAdmin || isED || isZoneAdmin)
                                                    ? [ALL_CHAPTERS_OPTION, ...chapters.map(c => ({ value: c._id, label: c.chapterName }))]
                                                    : chapters.map(c => ({ value: c._id, label: c.chapterName }))
                                            }
                                            value={
                                                formData.chapterId.includes('ALL')
                                                    ? [ALL_CHAPTERS_OPTION]
                                                    : chapters.filter(c => formData.chapterId.includes(c._id)).map(c => ({ value: c._id, label: c.chapterName }))
                                            }
                                            onChange={(selectedOptions) => {
                                                if (!selectedOptions || selectedOptions.length === 0) {
                                                    setFormData(prev => ({ ...prev, chapterId: [] }));
                                                    return;
                                                }
                                                // If user selects "All Chapters", clear everything else
                                                const hasAll = selectedOptions.some(opt => opt.value === 'ALL');
                                                if (hasAll) {
                                                    setFormData(prev => ({ ...prev, chapterId: ['ALL'] }));
                                                } else {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        chapterId: selectedOptions.map(opt => opt.value)
                                                    }));
                                                }
                                            }}
                                            isDisabled={loading || !formData.zoneId}
                                            placeholder="Select Chapter(s)"
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                        {formData.chapterId.includes('ALL') && (
                                            <small className="text-success mt-1 d-block">
                                                ✅ This user can access all current and future chapters in the selected zone.
                                            </small>
                                        )}
                                    </div>

                                                                        </div>
                                    <div className="d-flex align-items-center justify-content-center gap-3">
                                        <button
                                            type="button"
                                            className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                                            onClick={() => navigate("/users-list")}
                                            disabled={loading}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary grip text-md px-56 py-12 radius-8"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                            ) : isEditMode ? 'Update' : 'Save'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ToastContainer />
        </div>
    );
};

export default AddUserLayer;